import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Award,
  ArrowLeft,
  Lock,
  CheckCircle,
  Circle,
  Crown,
  BookOpen,
  Beaker,
  Target,
  Download,
  Loader2,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

const CertificationPage = () => {
  const navigate = useNavigate();
  const { user, fetchUser } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  const effectiveTier = user?.effective_tier || user?.tier || 'free';

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      const response = await api.getCertificationProgress();
      setProgress(response.data);
    } catch (error) {
      console.error('Error loading certification progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimCertificate = async () => {
    setClaiming(true);
    try {
      const response = await api.claimCertificate();
      toast.success('Certificate claimed successfully!');
      loadProgress();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not claim certificate');
    } finally {
      setClaiming(false);
    }
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const response = await api.createCheckout('pro_monthly', window.location.origin);
      
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
        setUpgrading(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout');
      setUpgrading(false);
    }
  };

  const handleDownloadCertificate = async () => {
    if (!user?.certificate_id) {
      toast.error('No certificate found');
      return;
    }
    
    setDownloading(true);
    try {
      const response = await api.downloadCertificate(user.certificate_id);
      // Create a blob and download
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `minnow_certificate_${user.certificate_id.substring(0, 8)}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Certificate downloaded! Open in browser and print to PDF.');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download certificate');
    } finally {
      setDownloading(false);
    }
  };

  const requirements = [
    {
      key: 'modules',
      label: 'Complete 5 Modules',
      icon: BookOpen,
      current: progress?.modules_completed || 0,
      required: progress?.modules_required || 5,
      color: 'bg-ocean-blue'
    },
    {
      key: 'labs',
      label: 'Complete 3 Labs',
      icon: Beaker,
      current: progress?.labs_completed || 0,
      required: progress?.labs_required || 3,
      color: 'bg-coral'
    },
    {
      key: 'pathway',
      label: 'Complete 1 Pathway',
      icon: Target,
      current: progress?.pathways_completed || 0,
      required: progress?.pathways_required || 1,
      color: 'bg-mint'
    },
    {
      key: 'readiness',
      label: 'Readiness Score ≥ 70',
      icon: Award,
      current: progress?.readiness_score || 0,
      required: progress?.readiness_threshold || 70,
      color: 'bg-sky-blue',
      isScore: true
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ocean-blue" />
      </div>
    );
  }

  // Not Pro tier - show upgrade prompt
  if (!progress?.eligible) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => navigate('/dashboard')}
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="flex items-center gap-2">
                  <Award className="w-6 h-6 text-coral" />
                  <span className="font-bold text-foreground">Certification</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-24 h-24 rounded-3xl bg-coral/10 flex items-center justify-center mx-auto mb-8">
              <Lock className="w-12 h-12 text-coral" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-4">
              Certification Requires Pro
            </h1>
            <p className="text-lg text-muted-foreground max-w-md mx-auto mb-8">
              Earn your Minnow School Certificate by upgrading to Pro tier. Showcase your founder skills to investors and partners.
            </p>
            <Card className="max-w-md mx-auto p-6 rounded-2xl border-2 border-coral/30">
              <div className="flex items-center gap-4 mb-4">
                <Crown className="w-8 h-8 text-coral" />
                <div className="text-left">
                  <h3 className="font-semibold">Pro Benefits</h3>
                  <p className="text-sm text-muted-foreground">$99/month</p>
                </div>
              </div>
              <ul className="text-sm text-left space-y-2 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-mint" />
                  <span>Certification system with PDF certificate</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-mint" />
                  <span>Advanced modules (Fundraising, Scaling)</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-mint" />
                  <span>All labs including Investor Readiness</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-mint" />
                  <span>Priority support</span>
                </li>
              </ul>
              <Button 
                className="w-full bg-coral hover:bg-coral/90"
                onClick={handleUpgrade}
                disabled={upgrading}
              >
                {upgrading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Crown className="w-4 h-4 mr-2" />}
                Upgrade to Pro
              </Button>
            </Card>
          </motion.div>
        </main>
      </div>
    );
  }

  // Pro tier - show certification progress
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Award className="w-6 h-6 text-coral" />
                <span className="font-bold text-foreground">Certification</span>
              </div>
            </div>
            <Badge className="bg-coral">
              <Crown className="w-3 h-3 mr-1" />
              Pro
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Minnow School Certification
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Complete the requirements below to earn your certificate and showcase your founder skills.
          </p>
        </motion.div>

        {/* Certificate Earned */}
        {progress?.certificate_earned && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-12"
          >
            <Card className="p-8 rounded-3xl bg-gradient-to-br from-coral via-coral/90 to-coral/80 border-0 text-center text-white">
              <Award className="w-16 h-16 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">
                Congratulations! 
              </h2>
              <p className="text-white/90 mb-6">
                You've earned your Minnow School Certificate!
              </p>
              <Button 
                size="lg"
                className="rounded-full bg-white text-coral hover:bg-white/90"
                data-testid="download-certificate-btn"
                onClick={handleDownloadCertificate}
                disabled={downloading}
              >
                {downloading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    Download Certificate
                  </>
                )}
              </Button>
            </Card>
          </motion.div>
        )}

        {/* Requirements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-xl font-bold text-foreground mb-6">Requirements</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {requirements.map((req, index) => {
              const completed = req.isScore ? req.current >= req.required : req.current >= req.required;
              const progressPercent = Math.min(100, (req.current / req.required) * 100);
              
              return (
                <motion.div
                  key={req.key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                >
                  <Card className={`rounded-xl border-2 ${completed ? 'border-mint bg-mint/5' : 'border-border'}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 rounded-xl ${req.color} flex items-center justify-center`}>
                          <req.icon className="w-6 h-6 text-white" />
                        </div>
                        {completed ? (
                          <CheckCircle className="w-6 h-6 text-mint" />
                        ) : (
                          <Circle className="w-6 h-6 text-muted-foreground" />
                        )}
                      </div>
                      <h3 className="font-semibold text-foreground mb-2">{req.label}</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-medium">
                            {req.isScore ? `${req.current}/100` : `${req.current}/${req.required}`}
                          </span>
                        </div>
                        <Progress value={progressPercent} className="h-2" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Claim Certificate Button */}
        {!progress?.certificate_earned && progress?.can_earn_certificate && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-12 text-center"
          >
            <Card className="p-8 rounded-3xl bg-gradient-to-br from-mint via-mint/90 to-mint/80 border-0">
              <Sparkles className="w-12 h-12 mx-auto mb-4 text-ocean-blue" />
              <h2 className="text-2xl font-bold text-ocean-blue mb-2">
                You&apos;ve Met All Requirements!
              </h2>
              <p className="text-ocean-blue/80 mb-6">
                Claim your certificate now and celebrate your achievement.
              </p>
              <Button 
                size="lg"
                onClick={handleClaimCertificate}
                disabled={claiming}
                className="rounded-full gradient-ocean text-white"
                data-testid="claim-certificate-btn"
              >
                {claiming ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Claiming...
                  </>
                ) : (
                  <>
                    <Award className="w-5 h-5 mr-2" />
                    Claim Certificate
                  </>
                )}
              </Button>
            </Card>
          </motion.div>
        )}

        {/* Not Ready Yet */}
        {!progress?.certificate_earned && !progress?.can_earn_certificate && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-12"
          >
            <Card className="p-6 rounded-2xl bg-muted/30">
              <div className="text-center">
                <Target className="w-10 h-10 mx-auto mb-3 text-ocean-blue" />
                <h3 className="font-semibold text-foreground mb-2">Keep Going!</h3>
                <p className="text-sm text-muted-foreground">
                  Complete the remaining requirements above to earn your certificate.
                </p>
              </div>
            </Card>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default CertificationPage;
